import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { Plus, Trash2, UserPlus, Shield, Lock } from "react-feather";
import {
  IGroup,
  IGroupDetailed,
  IPermission,
  IUser,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import { useGetLazy, usePost, usePut, useDelete } from "../../api";
import { usePermissions } from "../../hooks";
import Select, { SingleValue } from "react-select";

const RESOURCE_LABELS: Record<PermissionResource, string> = {
  [PermissionResource.LiveTemplate]: "Live Templates (put live)",
  [PermissionResource.AgenticWorkflow]: "Agentic Workflows (re-run)",
  [PermissionResource.Template]: "Templates (staging)",
  [PermissionResource.AffiliateProduct]: "Affiliate Products",
  [PermissionResource.Task]: "Tasks",
  [PermissionResource.CheckoutPlatform]: "Checkout Platform",
  [PermissionResource.KindredData]: "Kindred Data",
  [PermissionResource.FullAutoCouponsTargeting]: "Full Auto Coupons Targeting",
  [PermissionResource.TestTools]: "Test Tools",
  [PermissionResource.GroupMembership]: "Group Membership (own groups)",
  [PermissionResource.BasketTesting]: "Basket Testing",
  [PermissionResource.QaList]: "QA Lists",
  [PermissionResource.WorkPipeline]: "Work Pipeline",
  [PermissionResource.Results]: "Results",
  [PermissionResource.Reporting]: "Reporting",
  [PermissionResource.Merchant]: "Merchants",
  [PermissionResource.UserGroup]: "User Groups (admin)",
  [PermissionResource.OpenData]: "Open Data",
  [PermissionResource.CouponBulkUpdate]: "Coupon Bulk Updates",
  [PermissionResource.DomainReview]: "Domain Review",
  [PermissionResource.SmartShopping]: "Smart Shopping (demo agent)",
  [PermissionResource.Partners]: "Partners (live partner configuration)",
  [PermissionResource.BlacklistedDomain]: "Blacklisted Domains",
};

const ACTION_COLUMNS: { action: PermissionAction; label: string }[] = [
  { action: PermissionAction.CreateAny, label: "Create" },
  { action: PermissionAction.ReadAny, label: "Read" },
  { action: PermissionAction.UpdateAny, label: "Update" },
  { action: PermissionAction.DeleteAny, label: "Delete" },
];

const permKey = (action: PermissionAction, resource: PermissionResource) =>
  `${action}|${resource}`;

const toPermissionSet = (permissions: IPermission[]): Set<string> =>
  new Set(permissions.map((p) => permKey(p.action, p.resource)));

const fromPermissionSet = (set: Set<string>): IPermission[] =>
  Array.from(set).map((key) => {
    const [action, resource] = key.split("|");
    return {
      action: action as PermissionAction,
      resource: resource as PermissionResource,
      attributes: "*",
    };
  });

interface PermissionMatrixProps {
  value: Set<string>;
  onChange?: (next: Set<string>) => void;
  readOnly?: boolean;
}

interface UserOption {
  value: string;
  label: string;
}

const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  value,
  onChange,
  readOnly,
}) => {
  const toggle = (action: PermissionAction, resource: PermissionResource) => {
    if (readOnly || !onChange) return;
    const next = new Set(value);
    const key = permKey(action, resource);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  return (
    <Table size="sm" bordered responsive className="mb-0">
      <thead>
        <tr>
          <th>Resource</th>
          {ACTION_COLUMNS.map((c) => (
            <th key={c.action} className="text-center">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Object.values(PermissionResource).map((resource) => (
          <tr key={resource}>
            <td>{RESOURCE_LABELS[resource]}</td>
            {ACTION_COLUMNS.map((c) => (
              <td key={c.action} className="text-center">
                <Form.Check
                  type="checkbox"
                  checked={value.has(permKey(c.action, resource))}
                  disabled={readOnly}
                  onChange={() => toggle(c.action, resource)}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export const Groups: React.FC = () => {
  const { isAdmin } = usePermissions();
  const { execute: fetchGroups, loading: loadingGroups } =
    useGetLazy<IGroup[]>();
  const { execute: fetchGroup } = useGetLazy<IGroupDetailed>();
  const { execute: fetchUsers } = useGetLazy<IUser[]>();
  const { execute: createGroup } = usePost<IGroup>();
  const { execute: updateGroup } = usePut<IGroup>();
  const { execute: deleteGroup } = useDelete<void>();
  const { execute: addMember } = usePost<IGroupDetailed>();
  const { execute: removeMember } = useDelete<IGroupDetailed>();

  const [groups, setGroups] = useState<IGroup[]>([]);
  const [allUsers, setAllUsers] = useState<IUser[]>([]);
  const [selected, setSelected] = useState<IGroupDetailed | null>(null);
  const [editPerms, setEditPerms] = useState<Set<string>>(new Set());
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newPerms, setNewPerms] = useState<Set<string>>(new Set());

  const loadGroups = useCallback(async () => {
    try {
      const [g, u] = await Promise.all([
        fetchGroups("/groups"),
        fetchUsers("/groups/users"),
      ]);
      setGroups(g ?? []);
      setAllUsers(u ?? []);
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to load groups");
    }
  }, [fetchGroups, fetchUsers]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const selectGroup = async (id: string) => {
    setError(null);
    setSelectedUser("");
    try {
      const detail = await fetchGroup(`/groups/${id}`);
      setSelected(detail);
      setEditPerms(toPermissionSet(detail?.permissions ?? []));
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to load group");
    }
  };

  const candidates = useMemo(() => {
    if (!selected) return [];
    const memberIds = new Set(selected.users.map((u) => u._id));
    return allUsers.filter((u) => !memberIds.has(u._id));
  }, [selected, allUsers]);

  const candidateOptions = useMemo<UserOption[]>(
    () =>
      candidates.map((u) => ({
        value: u._id,
        label: `${u.username} (${u.role})`,
      })),
    [candidates]
  );

  const handleSavePermissions = async () => {
    if (!selected || selected.isSystem) return;
    setSaving(true);
    setError(null);
    try {
      await updateGroup(`/groups/${selected._id}`, {
        permissions: fromPermissionSet(editPerms),
      });
      await selectGroup(selected._id);
      await loadGroups();
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async () => {
    if (!selected || !selectedUser) return;
    setError(null);
    try {
      await addMember(`/groups/${selected._id}/members`, {
        userId: selectedUser,
      });
      setSelectedUser("");
      await selectGroup(selected._id);
      await loadGroups();
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to add member");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selected) return;
    setError(null);
    try {
      await removeMember(`/groups/${selected._id}/members/${userId}`);
      await selectGroup(selected._id);
      await loadGroups();
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to remove member");
    }
  };

  const handleDeleteGroup = async () => {
    if (!selected || selected.isSystem) return;
    if (!window.confirm(`Delete group "${selected.name}"?`)) return;
    setError(null);
    try {
      await deleteGroup(`/groups/${selected._id}`);
      setSelected(null);
      await loadGroups();
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to delete group");
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setError(null);
    try {
      await createGroup("/groups", {
        name: newName.trim(),
        description: newDescription.trim(),
        permissions: fromPermissionSet(newPerms),
      });
      setShowCreate(false);
      setNewName("");
      setNewDescription("");
      setNewPerms(new Set());
      await loadGroups();
    } catch (e: any) {
      setError(e?.data?.error || e?.message || "Failed to create group");
    }
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-3">
        <Col className="d-flex align-items-center justify-content-between">
          <div>
            <h2 className="mb-1 d-flex align-items-center gap-2">
              <Shield size={26} /> Groups &amp; Permissions
            </h2>
            <p className="text-muted mb-0">
              Manage access control groups and assign members.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowCreate(true)}
            className="d-flex align-items-center gap-2"
            hidden={!isAdmin}
          >
            <Plus size={16} /> New group
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col md={4} lg={3}>
          <Card className="shadow-sm">
            <Card.Header>Groups</Card.Header>
            {loadingGroups ? (
              <div className="p-4 text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <ListGroup variant="flush">
                {groups.map((g) => (
                  <ListGroup.Item
                    key={g._id}
                    action
                    active={selected?._id === g._id}
                    onClick={() => selectGroup(g._id)}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <span className="d-flex align-items-center gap-2">
                      {g.isSystem && <Lock size={14} />}
                      {g.name}
                    </span>
                    <Badge bg="secondary">{g.memberCount}</Badge>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card>
        </Col>

        <Col md={8} lg={9}>
          {!selected ? (
            <Card className="shadow-sm">
              <Card.Body className="text-muted text-center py-5">
                Select a group to view its members and permissions.
              </Card.Body>
            </Card>
          ) : (
            <>
              <Card className="shadow-sm mb-3">
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <span className="d-flex align-items-center gap-2">
                    {selected.isSystem && <Lock size={14} />}
                    <strong>{selected.name}</strong>
                    {selected.isSystem && <Badge bg="info">System</Badge>}
                  </span>
                  {isAdmin && !selected.isSystem && (
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={handleDeleteGroup}
                      className="d-flex align-items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  {selected.description && (
                    <p className="text-muted">{selected.description}</p>
                  )}

                  {selected.canManageMembers && (
                    <Form className="d-flex gap-2 align-items-end flex-wrap mb-3">
                      <Form.Group style={{ minWidth: 280 }}>
                        <Form.Label>Add member</Form.Label>
                        <Select<UserOption, false>
                          options={candidateOptions}
                          value={
                            candidateOptions.find(
                              (option) => option.value === selectedUser
                            ) ?? null
                          }
                          onChange={(option: SingleValue<UserOption>) =>
                            setSelectedUser(option?.value ?? "")
                          }
                          isClearable
                          isSearchable
                          placeholder="Type to search users..."
                          noOptionsMessage={() => "No users found"}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: 38,
                              fontSize: "0.9rem",
                            }),
                          }}
                        />
                        <Form.Text className="text-muted">
                          {candidateOptions.length} users available
                        </Form.Text>
                      </Form.Group>
                      <Button
                        variant="primary"
                        onClick={handleAddMember}
                        disabled={!selectedUser}
                        className="d-flex align-items-center gap-2"
                      >
                        <UserPlus size={16} /> Add
                      </Button>
                    </Form>
                  )}

                  <Table hover responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Role</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.users.length ? (
                        selected.users.map((u) => (
                          <tr key={u._id}>
                            <td>{u.username}</td>
                            <td>
                              <Badge bg="light" text="dark">
                                {u.role}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {selected.canManageMembers && (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleRemoveMember(u._id)}
                                  className="d-flex align-items-center gap-1 ms-auto"
                                >
                                  <Trash2 size={14} /> Remove
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="text-center text-muted py-3"
                          >
                            No members yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {isAdmin && (
                <Card className="shadow-sm">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <span>Permissions</span>
                    {selected.isSystem ? (
                      <Badge bg="secondary">Read-only (system group)</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="success"
                        onClick={handleSavePermissions}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save permissions"}
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <PermissionMatrix
                      value={editPerms}
                      onChange={selected.isSystem ? undefined : setEditPerms}
                      readOnly={selected.isSystem}
                    />
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Col>
      </Row>

      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create custom group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Content Editors"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Label>Permissions</Form.Label>
          <PermissionMatrix value={newPerms} onChange={setNewPerms} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!newName.trim()}
          >
            Create group
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
